<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef } from 'vue'
import { useRoute, useRouter, type RouteLocationRaw } from 'vue-router'
import { storeToRefs } from 'pinia'
import PageHeader from '@/components/PageHeader.vue'
import WizardOverlayContainer from '@/components/WizardOverlayContainer.vue'
import type { UnfocusReason } from '@/components/WizardOverlayContainer.vue'
import WizardPrompt from '@/components/WizardPrompt.vue'
import GbDialog from '@/components/GbDialog.vue'
import GbButton from '@/components/GbButton.vue'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import type { ContentProfileSubject } from '@/contentprofile/premium_locks'
import { getGradesWithResultsForFilters } from '@/search/algolia_search'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'
import { betaOptIn } from '@/util/beta_opt_in'
import {
  answeredSelection,
  browseGoalsQuery,
  buildPrompts,
  EMPTY_SELECTION,
  redirectGrades,
  selectionFromQuery,
  selectionToQuery,
  withAnswer
} from './goal_wizard'
import type { GoalWizardField } from './goal_wizard'

const WIZARD_TITLE = 'Personalized Learning Goal Wizard'
const UNIVERSAL_STANDARD_SET = 'universal'

const route = useRoute()
const router = useRouter()
const analytics = getAnalyticsTracker()
const contentProfile = useContentProfileStore()
const { publishedSubjects } = storeToRefs(contentProfile)

const subjects = computed(() => publishedSubjects.value as ContentProfileSubject[])
const selection = computed(() => answeredSelection(subjects.value, selectionFromQuery(route.query)))
const prompts = computed(() => buildPrompts(subjects.value, selection.value))
// Analytics reports a level the wizard does not offer as linked, as Vue 2 does.
const reportedInstructionalLevel = computed(() => selection.value.instructionalLevel || selectionFromQuery(route.query).instructionalLevel)

const wizardFocus = ref(false)
const noGoalsFound = ref(false)
const isCountingGoals = ref(false)

function answer(field: GoalWizardField, value: string | string[] | null) {
  const answered = withAnswer(selectionFromQuery(route.query), field, String(value ?? ''))
  router.replace({ name: 'BrowseGoalsWizardModal', query: selectionToQuery(answered, route.query) })
}

function startOver() {
  return router.replace({ name: 'BrowseGoalsWizardModal', query: selectionToQuery(EMPTY_SELECTION, route.query) })
}

const subjectPrompt = useTemplateRef<InstanceType<typeof WizardPrompt>>('subjectPrompt')

// Start Over disappears once the answers are cleared, so focus would otherwise fall to the page body.
async function startOverFromTheFirstPrompt() {
  await startOver()
  await nextTick()
  subjectPrompt.value?.$el.querySelector('button')?.focus()
}

function unfocus(reason: UnfocusReason) {
  wizardFocus.value = false
  if (reason === 'close') {
    startOver()
  }
}

function standardSetsWithUniversal(subject: string): string[] {
  const standardSets = contentProfile.visibleStandardSetsForSubject(subject, { onlyPublished: true })
  if (standardSets.includes(UNIVERSAL_STANDARD_SET)) {
    return standardSets
  }
  return [...standardSets, UNIVERSAL_STANDARD_SET]
}

// Counted across every grade, so a grade without goals can fall back to the nearest lower one.
async function gradesWithResults(): Promise<string[]> {
  const { subject, category } = selection.value
  return getGradesWithResultsForFilters({ subject, subjectDomain: category || undefined }, standardSetsWithUniversal(subject))
}

// A handed-off submission is recorded once the URL is Browse Goals' but before that page records
// its visit, so the event describes the page it lands on and comes first; awaiting the push would
// record it after the visit. A hand-off that never arrives, or that a guard redirects elsewhere, records nothing.
async function pushRecordingOnArrival(location: RouteLocationRaw, record: () => void) {
  const destinationName = router.resolve(location).name
  const removeHook = router.afterEach((to, _from, failure) => {
    removeHook()
    if (!failure && to.name === destinationName) {
      record()
    }
  })
  try {
    await router.push(location)
  } finally {
    removeHook()
  }
}

// A second hand-off would cancel the first, and the cancellation removes both hand-offs' arrival
// hooks, so the wizard stays busy until its hand-off settles.
async function getResources() {
  if (isCountingGoals.value) {
    return
  }
  isCountingGoals.value = true
  try {
    await countGoalsAndHandOff()
  } finally {
    isCountingGoals.value = false
  }
}

async function countGoalsAndHandOff() {
  const { subject, category, grade } = selection.value
  const instructionalLevel = reportedInstructionalLevel.value
  let grades: string[]
  try {
    grades = redirectGrades(grade, await gradesWithResults())
  } catch (e) {
    console.error('Could not count the goals for the Goal Wizard', e)
    return
  }
  const recordSubmission = () => analytics.sendEvent(EVENT_NAMES.GOAL_WIZARD.SUBMITTED, {
    subject,
    subject_domain: category,
    grades: [grade],
    instructional_level: instructionalLevel
  })
  if (grades.length) {
    await pushRecordingOnArrival({ name: 'BrowseGoalsPage', query: browseGoalsQuery(selection.value, grades, route.query) }, recordSubmission)
  } else {
    noGoalsFound.value = true
    recordSubmission()
  }
}

function requestContent() {
  const { subject, category, grade } = selection.value
  analytics.sendEvent(EVENT_NAMES.GOAL_WIZARD.REQUESTED_CONTENT, {
    source: 'wizard',
    subject,
    subjectDomains: category,
    gradeLevel: grade,
    instructionalLevel: reportedInstructionalLevel.value
  })
  noGoalsFound.value = false
}

analytics.sendEvent(EVENT_NAMES.GOAL_WIZARD.VIEWED_PAGE, { ref: route.query.ref })
</script>

<template>
  <div class="container">
    <PageHeader
      :title="WIZARD_TITLE"
      subtitle="Use the wizard to discover targeted learning goals, objectives, and progress monitoring resources for an individual student."
    />

    <WizardOverlayContainer :activated="wizardFocus" :paused="noGoalsFound" :label="WIZARD_TITLE" @unfocus="unfocus">
      <div class="mb-5 min-h-5">
        <button
          v-if="wizardFocus && selection.subject"
          data-testid="start-over"
          type="button"
          class="inline-flex items-center gap-1 text-text-md text-blue-400 underline-offset-4 hover:underline focus-visible:outline-offset-2"
          @click="startOverFromTheFirstPrompt"
        >
          <span class="material-icons-round text-[1em]" aria-hidden="true">undo</span>
          Start Over
        </button>
      </div>

      <div class="mb-2.5 space-y-2.5" data-testid="wizard-sentences">
        <WizardPrompt
          ref="subjectPrompt"
          :filter="prompts.subject"
          before="I need a learning goal in"
          :clearable="false"
          @open="wizardFocus = true"
          @select="answer('subject', $event)"
        />

        <template v-if="selection.subject">
          <WizardPrompt
            :filter="prompts.category"
            before="specifically in"
            :clearable="false"
            @open="wizardFocus = true"
            @select="answer('category', $event)"
          />
          <WizardPrompt
            :filter="prompts.grade"
            before="for a student in grade"
            :clearable="false"
            @open="wizardFocus = true"
            @select="answer('grade', $event)"
          />
          <WizardPrompt
            v-if="selection.grade"
            :filter="prompts.instructionalLevel"
            before="who can access grade level content with"
            after="individualized support"
            :clearable="false"
            @open="wizardFocus = true"
            @select="answer('instructionalLevel', $event)"
          />
        </template>
      </div>

      <div v-if="selection.subject" class="text-center">
        <button
          v-if="selection.grade"
          data-testid="get-resources"
          type="button"
          :aria-disabled="isCountingGoals"
          class="mt-[30px] whitespace-normal break-words rounded-xs border border-transparent bg-blue-400 px-4 py-2.5 text-display-sm leading-8 text-white hover:bg-blue-500 focus-visible:outline-offset-2"
          @click="getResources"
        >
          Get resources for this student
        </button>
      </div>

      <div class="my-6 text-right">
        <router-link
          v-if="!wizardFocus"
          :to="{ name: 'BrowseGoalsPage', query: betaOptIn(route.query) }"
          class="text-text-md text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
        >
          Skip the wizard
        </router-link>
      </div>
    </WizardOverlayContainer>

    <GbDialog v-model:visible="noGoalsFound" dialog-id="no-goals-found" header="No goals found" footer-buttons>
      <template #bodyContent>
        <p>
          Sorry, we currently do not have goals that match your criteria. Click the button below to request this
          content, and try a different search.
        </p>
      </template>
      <template #footerButtonLeft>
        <GbButton type="button" gb-theme="grayOutlineSecondary" label="Close" @click="noGoalsFound = false" />
      </template>
      <template #footerButtonRight>
        <GbButton type="button" gb-theme="bluePrimary" label="Request Content" @click="requestContent" />
      </template>
    </GbDialog>
  </div>
</template>
